import { useState, useRef, useEffect } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { workOrdersApi, committentiApi, tecniciApi, orariAperturaApi, authApi, whatsappApi, redApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { AppuntamentoChip } from '../components/AppuntamentoChip'
import { MultiSelectDropdown } from '../components/MultiSelectDropdown'
import VerificaFrazionarioModal from '../components/pratiche/VerificaFrazionarioModal'
import RiparaIndirizziWizard from '../components/pratiche/RiparaIndirizziWizard'
import PianificaGiroModal from '../components/pratiche/PianificaGiroModal'
import EmailAccessiModal from '../components/pratiche/EmailAccessiModal'
import { Plus, Search, RefreshCw, ChevronLeft, ChevronRight, Upload, Download, Trash2, Eye, ArrowUp, ArrowDown, ArrowUpDown, CalendarClock, Tag, Calendar, X, Pencil, Check, Table2, Navigation, AlertTriangle, Home, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import type { VerificaFrazionarioResult } from '../types/index'

const STATI = ['ricevuto', 'assegnato', 'sospeso', 'annullato', 'eseguito']

const STATO_COLORS: Record<string, string> = {
  ricevuto:  '#3b82f6',
  assegnato: '#f59e0b',
  sospeso:   '#6b7280',
  annullato: '#ef4444',
  eseguito:  '#22c55e',
}

function StatoBadge({ stato }: { stato?: string }) {
  const color = stato ? STATO_COLORS[stato] ?? '#6b7280' : '#6b7280'
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      background: color + '22',
      color,
      textTransform: 'capitalize',
    }}>
      {stato?.replace(/_/g, ' ') ?? '—'}
    </span>
  )
}

type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortBy, sortDir }: { col: string; sortBy: string; sortDir: SortDir }) {
  if (sortBy !== col) return <ArrowUpDown size={11} className="ml-1 opacity-30" />
  return sortDir === 'asc' ? <ArrowUp size={11} className="ml-1 text-blue-500" /> : <ArrowDown size={11} className="ml-1 text-blue-500" />
}

export default function Pratiche() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '')
  const [statoFiltri, setStatoFiltri] = useState<string[]>(() => searchParams.get('stato')?.split(',').filter(Boolean) ?? [])
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort_by') ?? 'geo')
  const [sortDir, setSortDir] = useState<SortDir>(() => (searchParams.get('sort_dir') === 'desc' ? 'desc' : 'asc'))
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [pianificaWO, setPianificaWO] = useState<any | null>(null)
  const [showBulkTipologia, setShowBulkTipologia] = useState(false)
  const [showBulkEditModal, setShowBulkEditModal] = useState(false)
  const [verificaLoading, setVerificaLoading] = useState(false)
  // Anteprima correzioni (Verifica vs Poste, Correggi Codice/Nome Sito, Ripara CAP/Prov.):
  // nulla viene scritto sul DB finché l'utente non conferma nel modal
  const [anteprima, setAnteprima] = useState<{
    result: VerificaFrazionarioResult
    titolo: string
    sottotitolo?: string
    labelNonTrovati?: string
    motivo?: string
    preselezionaTutto?: boolean
  } | null>(null)
  const [exportPianModalOpen, setExportPianModalOpen] = useState(false)
  const [exportPianDa, setExportPianDa] = useState('')
  const [exportPianA, setExportPianA] = useState('')
  const [exportPianLoading, setExportPianLoading] = useState(false)
  const [exportPianRegioni, setExportPianRegioni] = useState<string[]>([])
  const [exportPianTecnicoIds, setExportPianTecnicoIds] = useState<string[]>([])
  const [exportPianStep, setExportPianStep] = useState<'filtri' | 'destinatari'>('filtri')
  const [exportPianCanale, setExportPianCanale] = useState<'whatsapp' | 'email'>('whatsapp')
  const [exportPianContatti, setExportPianContatti] = useState<{ id: number; nome: string; cognome: string; email?: string | null; telefono_whatsapp?: string | null }[]>([])
  const [exportPianDestinatari, setExportPianDestinatari] = useState<Set<number>>(new Set())
  const [exportPianSending, setExportPianSending] = useState(false)
  const [emailAccessiOpen, setEmailAccessiOpen] = useState(false)
  const [percorsoOpen, setPercorsoOpen] = useState(false)
  const [percorsoGps, setPercorsoGps] = useState<{ lat: number; lng: number } | null>(null)
  const [percorsoGpsFallback, setPercorsoGpsFallback] = useState(false)
  const [percorsoStatus, setPercorsoStatus] = useState<'idle' | 'loading' | 'ready' | 'no_gps'>('idle')
  const [percorsoTab, setPercorsoTab] = useState<'attuale' | 'ottimale'>('attuale')
  const [tecnicoFiltri, setTecnicoFiltri] = useState<string[]>(() => searchParams.get('tecnico')?.split(',').filter(Boolean) ?? [])
  const [committenteFiltro, setCommittenteFiltro] = useState(() => searchParams.get('committente') ?? '')
  const [regioneFiltro, setRegioneFiltro] = useState(() => searchParams.get('regione') ?? '')
  const [comuneFiltri, setComuneFiltri] = useState<string[]>(() => searchParams.get('comune')?.split(',').filter(Boolean) ?? [])
  const [lottoFiltro, setLottoFiltro] = useState(() => searchParams.get('lotto') ?? '')
  const [dataFiltroFrom, setDataFiltroFrom] = useState(() => searchParams.get('data_da') ?? '')
  const [dataFiltroTo, setDataFiltroTo] = useState(() => searchParams.get('data_a') ?? '')

  // Mantiene i filtri nell'URL (replace, senza nuove voci nella history) così
  // il tasto indietro del browser dopo aver aperto una pratica torna a una
  // URL che li contiene già, invece di rismontare la pagina con stato vuoto.
  useEffect(() => {
    const next: Record<string, string> = {}
    if (search) next.search = search
    if (statoFiltri.length) next.stato = statoFiltri.join(',')
    if (sortBy !== 'geo') next.sort_by = sortBy
    if (sortDir !== 'asc') next.sort_dir = sortDir
    if (tecnicoFiltri.length) next.tecnico = tecnicoFiltri.join(',')
    if (committenteFiltro) next.committente = committenteFiltro
    if (regioneFiltro) next.regione = regioneFiltro
    if (comuneFiltri.length) next.comune = comuneFiltri.join(',')
    if (lottoFiltro) next.lotto = lottoFiltro
    if (dataFiltroFrom) next.data_da = dataFiltroFrom
    if (dataFiltroTo) next.data_a = dataFiltroTo
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statoFiltri, sortBy, sortDir, tecnicoFiltri, committenteFiltro, regioneFiltro, comuneFiltri, lottoFiltro, dataFiltroFrom, dataFiltroTo])

  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [editingNumeroId, setEditingNumeroId] = useState<number | null>(null)
  const [editingNumeroVal, setEditingNumeroVal] = useState('')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')
  const isReadonly = user?.role === 'readonly'
  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const oggiMode = statoFiltri.length === 1 && statoFiltri[0] === 'assegnato' && dataFiltroFrom === todayStr && dataFiltroTo === todayStr && sortBy === 'tecnico_ordine'

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const toggleSelect = (id: number) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () => setSelected(s => s.size === filteredItems.length ? new Set() : new Set(filteredItems.map((w: any) => w.id)))

  const params = {
    page: 1, per_page: 9999,
    tipo_intervento: 'standard',
    ...(search && { search }),
    ...(committenteFiltro && { committente_id: Number(committenteFiltro) }),
    sort_by: sortBy,
    sort_dir: sortDir,
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['work-orders-standard', params],
    queryFn: () => workOrdersApi.list(params).then(r => r.data),
  })

  const total = data?.total ?? 0
  const items: any[] = data?.items ?? []

  const regioniDisponibili = [...new Set(items.map((w: any) => w.cliente_regione).filter(Boolean))].sort() as string[]
  const comuniDisponibili = [...new Set(items.map((w: any) => w.cliente_comune).filter(Boolean))].sort() as string[]
  const lottiDisponibili = [...new Set(items.map((w: any) => w.lotto).filter(Boolean))].sort() as string[]

  const filteredItems = items.filter((w: any) => {
    if (statoFiltri.length && !statoFiltri.includes(w.stato)) return false
    if (tecnicoFiltri.length && !tecnicoFiltri.includes(String(w.tecnico_id))) return false
    if (regioneFiltro && (w.cliente_regione ?? '') !== regioneFiltro) return false
    if (comuneFiltri.length && !comuneFiltri.includes(w.cliente_comune ?? '')) return false
    if (lottoFiltro && (w.lotto ?? '') !== lottoFiltro) return false
    if (dataFiltroFrom && w.data_appuntamento && w.data_appuntamento < dataFiltroFrom) return false
    if (dataFiltroTo && w.data_appuntamento && w.data_appuntamento > dataFiltroTo) return false
    if (dataFiltroFrom && !w.data_appuntamento) return false
    return true
  })

  // Pratiche da usare nel percorso: selezione esplicita (se presente) oppure tutti i filtrati
  // In entrambi i casi ordinate per ordine_giro, poi per numero_progressivo
  const pratichePercorso = (() => {
    const base = selected.size > 0
      ? filteredItems.filter((w: any) => selected.has(w.id))
      : filteredItems
    return [...base].sort((a: any, b: any) => {
      if (a.ordine_giro != null && b.ordine_giro != null) return a.ordine_giro - b.ordine_giro
      if (a.ordine_giro != null) return -1
      if (b.ordine_giro != null) return 1
      return (a.numero_progressivo ?? '').localeCompare(b.numero_progressivo ?? '')
    })
  })()

  const updateNumeroMutation = useMutation({
    mutationFn: ({ id, numero }: { id: number; numero: string }) =>
      workOrdersApi.update(id, { numero_progressivo: numero }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-standard'] })
      setEditingNumeroId(null)
      toast.success('Numero aggiornato')
    },
    onError: () => toast.error('Errore aggiornamento numero'),
  })

  const deleteAllMutation = useMutation({
    mutationFn: () => workOrdersApi.deleteAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success('Tutte le pratiche eliminate')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const deleteOneMutation = useMutation({
    mutationFn: (id: number) => workOrdersApi.deleteOne(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success('Pratica eliminata')
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore eliminazione'),
  })

  const [riparaIds, setRiparaIds] = useState<number[] | null>(null)
  const [giroIds, setGiroIds] = useState<number[] | null>(null)
  const [correzioneCodiceRunning, setCorrezioneCodiceRunning] = useState(false)
  const [correzioneNomeRunning, setCorrezioneNomeRunning] = useState(false)
  const [exportMese, setExportMese] = useState(() => new Date().toISOString().slice(0, 7))
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportRegione, setExportRegione] = useState('')
  const [exportTecnicoId, setExportTecnicoId] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [modalitaTabella, setModalitaTabella] = useState(false)
  const [righeModificate, setRigheModificate] = useState<Record<number, Record<string, any>>>({})
  const [salvataggioLoading, setSalvataggioLoading] = useState(false)
  const [ordiniLocali, setOrdiniLocali] = useState<Record<number, string>>({})

  const salvaOrdineMutation = useMutation({
    mutationFn: (ordini: { wo_id: number; ordine: number }[]) =>
      workOrdersApi.salvaOrdineGiro({ ordini }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-standard'] })
      toast.success('Ordine interventi salvato')
    },
    onError: () => toast.error('Errore salvataggio ordine'),
  })

  const saveOrdine = (woId: number, val: string) => {
    const n = parseInt(val, 10)
    if (!val.trim() || isNaN(n)) return
    salvaOrdineMutation.mutate([{ wo_id: woId, ordine: n }])
    setOrdiniLocali(prev => { const next = { ...prev }; delete next[woId]; return next })
  }

  const setCellaTabella = (id: number, field: string, value: any, original: any) => {
    setRigheModificate(prev => {
      const rowMods = { ...prev[id], [field]: value }
      if (value === original || (value === '' && original == null) || (value === null && original == null)) {
        delete rowMods[field]
      }
      if (Object.keys(rowMods).length === 0) {
        const { [id]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [id]: rowMods }
    })
  }

  const handleSalvaTabella = async () => {
    const entries = Object.entries(righeModificate)
    if (!entries.length) return
    setSalvataggioLoading(true)
    let ok = 0, err = 0
    for (const [idStr, changes] of entries) {
      try { await workOrdersApi.update(Number(idStr), changes); ok++ }
      catch { err++ }
    }
    setSalvataggioLoading(false)
    queryClient.invalidateQueries({ queryKey: ['work-orders-standard'] })
    if (err === 0) {
      toast.success(`${ok} pratiche aggiornate`)
      setRigheModificate({})
      setModalitaTabella(false)
    } else {
      toast.error(`${ok} aggiornate, ${err} errori`)
      setRigheModificate({})
    }
  }

  const fmtHM = (d: Date) => d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  const haversineP = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371; const dLat = (lat2 - lat1) * Math.PI / 180; const dLon = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }
  const calcolaStepsP = (gps: { lat: number; lng: number }, wos: any[]) => {
    const START = new Date(); START.setHours(8, 0, 0, 0)
    let cur = new Date(START); let curLat = gps.lat; let curLng = gps.lng
    const steps = wos.map((wo: any) => {
      let distanza_km: number | null = null; let tempo_viaggio_min: number | null = null
      if (wo.cliente_lat != null && wo.cliente_lng != null) {
        distanza_km = haversineP(curLat, curLng, wo.cliente_lat, wo.cliente_lng)
        tempo_viaggio_min = Math.round(distanza_km / 50 * 60)
        cur = new Date(cur.getTime() + tempo_viaggio_min * 60000)
        curLat = wo.cliente_lat; curLng = wo.cliente_lng
      }
      const ora_arrivo = new Date(cur)
      cur = new Date(cur.getTime() + 35 * 60000)
      const ora_fine = new Date(cur)
      let chiusura_ok: boolean | null = null; let margine_min: number | null = null
      if (wo.ora_chiusura) {
        const [hh, mm] = wo.ora_chiusura.split(':').map(Number)
        const chiusura = new Date(ora_fine); chiusura.setHours(hh, mm, 0, 0)
        margine_min = Math.round((chiusura.getTime() - ora_fine.getTime()) / 60000)
        chiusura_ok = margine_min >= 0
      }
      return { wo, distanza_km, tempo_viaggio_min, ora_arrivo, ora_fine, chiusura_ok, margine_min }
    })
    let ritorno: any = null
    if (wos.some((w: any) => w.cliente_lat != null)) {
      const d = haversineP(curLat, curLng, gps.lat, gps.lng)
      ritorno = { distanza_km: d, tempo_viaggio_min: Math.round(d / 50 * 60), ora_arrivo: new Date(cur.getTime() + Math.round(d / 50 * 60) * 60000) }
    }
    const totKm = steps.reduce((s: number, st: any) => s + (st.distanza_km ?? 0), 0) + (ritorno?.distanza_km ?? 0)
    return { steps, ritorno, totKm }
  }
  const ottimizzaOrdineP = (gps: { lat: number; lng: number }, wos: any[]): any[] => {
    const withCoords = wos.filter((w: any) => w.cliente_lat != null && w.cliente_lng != null)
    const withoutCoords = wos.filter((w: any) => w.cliente_lat == null || w.cliente_lng == null)
    const result: any[] = []; const remaining = [...withCoords]
    let curLat = gps.lat; let curLng = gps.lng
    while (remaining.length) {
      const nearest = remaining.reduce((best: any, w: any) => haversineP(curLat, curLng, w.cliente_lat, w.cliente_lng) < haversineP(curLat, curLng, best.cliente_lat, best.cliente_lng) ? w : best)
      result.push(nearest); remaining.splice(remaining.indexOf(nearest), 1)
      curLat = nearest.cliente_lat; curLng = nearest.cliente_lng
    }
    return [...result, ...withoutCoords]
  }
  const calcolaCentroideP = () => {
    const withCoords = pratichePercorso.filter((w: any) => w.cliente_lat != null && w.cliente_lng != null)
    if (!withCoords.length) return null
    return { lat: withCoords.reduce((s: number, w: any) => s + w.cliente_lat, 0) / withCoords.length, lng: withCoords.reduce((s: number, w: any) => s + w.cliente_lng, 0) / withCoords.length }
  }
  const mapsUrlP = (gps: { lat: number; lng: number }, wos: any[]) => {
    const pts = wos.filter((w: any) => w.cliente_lat != null).map((w: any) => `${w.cliente_lat},${w.cliente_lng}`)
    if (!pts.length) return null
    const origin = `${gps.lat},${gps.lng}`; const dest = pts[pts.length - 1]; const wps = pts.slice(0, -1)
    const base = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`
    return wps.length ? `${base}&waypoints=${wps.join('|')}` : base
  }
  const apriPercorso = () => {
    setPercorsoOpen(true); setPercorsoStatus('loading'); setPercorsoGps(null); setPercorsoGpsFallback(false)
    const usaFallback = () => {
      const centro = calcolaCentroideP()
      if (centro) { setPercorsoGps(centro); setPercorsoGpsFallback(true); setPercorsoStatus('ready') }
      else setPercorsoStatus('no_gps')
    }
    if (!navigator.geolocation) { usaFallback(); return }
    try {
      navigator.geolocation.getCurrentPosition(
        pos => { setPercorsoGpsFallback(false); setPercorsoGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setPercorsoStatus('ready') },
        () => usaFallback(),
        { enableHighAccuracy: true, timeout: 8000 }
      )
    } catch { usaFallback() }
  }

  // Percorso ottimale: solo pratiche selezionate → pianificazione giornaliera (PianificaGiroModal)
  const runPercorsoOttimale = () => {
    if (selected.size === 0) { toast.error('Seleziona le pratiche da organizzare'); return }
    setGiroIds(Array.from(selected))
  }

  // Giorno proposto: data appuntamento più frequente tra le selezionate, altrimenti oggi
  const dataGiroIniziale = () => {
    const conta: Record<string, number> = {}
    for (const w of items) {
      if (selected.has(w.id) && w.data_appuntamento) conta[w.data_appuntamento] = (conta[w.data_appuntamento] ?? 0) + 1
    }
    return Object.entries(conta).sort((a, b) => b[1] - a[1])[0]?.[0] ?? todayStr
  }

  const idsFiltrati = () => filteredItems.map((w: any) => w.id as number)

  const runCorreggiCodiceSito = async () => {
    const ids = idsFiltrati()
    if (!ids.length) { toast.error('Nessuna pratica nei filtri correnti'); return }
    setCorrezioneCodiceRunning(true)
    try {
      const res = await workOrdersApi.correggiCodiceSito(ids)
      setAnteprima({
        result: res.data,
        titolo: 'Correggi Codice Sito',
        sottotitolo: `(${ids.length} pratiche filtrate)`,
        motivo: 'Codice sito copiato dal numero pratica (Correggi Codice Sito)',
        preselezionaTutto: true,
      })
    } catch {
      toast.error('Errore durante la correzione del codice sito')
    } finally {
      setCorrezioneCodiceRunning(false)
    }
  }

  const runCorreggiNomeSito = async () => {
    const ids = idsFiltrati()
    if (!ids.length) { toast.error('Nessuna pratica nei filtri correnti'); return }
    setCorrezioneNomeRunning(true)
    try {
      const res = await workOrdersApi.correggiNomeSito(ids)
      setAnteprima({
        result: res.data,
        titolo: 'Correggi Nome Sito',
        sottotitolo: `(${ids.length} pratiche filtrate)`,
        labelNonTrovati: 'Senza "Ufficio:" nelle note',
        motivo: "Nome sito copiato dall'Ufficio nelle note (Correggi Nome Sito)",
        preselezionaTutto: true,
      })
    } catch {
      toast.error('Errore durante la correzione del nome sito')
    } finally {
      setCorrezioneNomeRunning(false)
    }
  }

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-bulk'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const tecniciBulk: any[] = tecniciData ?? []

  const { data: committentiList } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const committenti: any[] = committentiList ?? []

  const handleVerificaFrazionarioPoste = async () => {
    setVerificaLoading(true)
    try {
      const res = await workOrdersApi.verificaFrazionarioPoste(Array.from(selected))
      setAnteprima({ result: res.data as VerificaFrazionarioResult, titolo: 'Verifica vs Poste' })
    } catch {
      toast.error('Errore durante la verifica frazionario (blia.it)')
    } finally {
      setVerificaLoading(false)
    }
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const [anno, mese] = exportMese.split('-').map(Number)
      const res = await workOrdersApi.exportXlsx(
        anno, mese,
        exportRegione || undefined,
        exportTecnicoId ? Number(exportTecnicoId) : undefined,
      )
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `pratiche_${exportMese}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
      setExportModalOpen(false)
    } catch {
      toast.error('Errore export')
    } finally {
      setExportLoading(false)
    }
  }

  const generaXlsxPianificazione = () => workOrdersApi.exportPianificazione(
    exportPianDa, exportPianA, undefined,
    exportPianTecnicoIds.map(Number),
    exportPianRegioni,
  )

  const handleExportPianificazione = async () => {
    if (!exportPianDa || !exportPianA) return
    setExportPianLoading(true)
    try {
      const res = await generaXlsxPianificazione()
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pianificazione_${exportPianDa}_${exportPianA}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setExportPianModalOpen(false)
      toast.success('Export pianificazione completato')
    } catch {
      toast.error('Errore durante l\'export')
    } finally {
      setExportPianLoading(false)
    }
  }

  const blobToBase64Pian = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

  const apriDestinatariPian = async (canale: 'whatsapp' | 'email') => {
    if (!exportPianDa || !exportPianA) { toast.error('Imposta il periodo'); return }
    setExportPianCanale(canale)
    setExportPianDestinatari(new Set())
    setExportPianStep('destinatari')
    try {
      const res = await authApi.whatsappContacts()
      const campo = canale === 'whatsapp' ? 'telefono_whatsapp' : 'email'
      setExportPianContatti(res.data.filter((u: any) => u[campo]))
    } catch {
      toast.error('Errore caricamento anagrafica utenti')
    }
  }

  const toggleDestinatarioPian = (id: number) => setExportPianDestinatari(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleInviaPian = async () => {
    if (exportPianDestinatari.size === 0) return
    setExportPianSending(true)
    try {
      const res = await generaXlsxPianificazione()
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const filename = `pianificazione_${exportPianDa}_${exportPianA}.xlsx`
      const destinatari = exportPianContatti.filter(c => exportPianDestinatari.has(c.id))
      if (exportPianCanale === 'whatsapp') {
        const base64 = await blobToBase64Pian(blob)
        let ok = 0
        for (const d of destinatari) {
          try {
            await whatsappApi.sendDocument(
              d.telefono_whatsapp as string, base64, filename,
              'Pianificazione interventi', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            )
            ok++
          } catch {
            toast.error(`Invio fallito a ${d.nome} ${d.cognome}`)
          }
        }
        if (ok > 0) toast.success(`XLSX inviato a ${ok} destinatari`)
      } else {
        const base64 = await blobToBase64Pian(blob)
        const resp = await redApi.export.inviaEmailAllegato({
          file_base64: base64,
          filename,
          oggetto: 'XSophia — Pianificazione interventi',
          corpo: 'In allegato il file di pianificazione interventi.',
          destinatari: destinatari.map(d => d.email as string),
        })
        if (resp.data.inviati > 0) toast.success(`XLSX inviato via email a ${resp.data.inviati} destinatari`)
        if (resp.data.inviati < resp.data.totale) toast.error(`${resp.data.totale - resp.data.inviati} invii falliti`)
      }
      setExportPianModalOpen(false)
      setExportPianStep('filtri')
    } catch {
      toast.error('Errore durante l\'invio')
    } finally {
      setExportPianSending(false)
    }
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Seleziona un file .xlsx')
      e.target.value = ''
      return
    }
    const lottoImport = window.prompt(
      'Indica il lotto di appartenenza di queste pratiche (es. lotto2, lotto3). Lascia vuoto per non taggarle.',
      ''
    )
    if (lottoImport === null) {
      e.target.value = ''
      return
    }
    if (!window.confirm(
      `Importare pratiche da "${file.name}"?\n` +
      `Le pratiche esistenti con stesso Frazionario verranno aggiornate.\n` +
      `Le nuove verranno create con stato "Ricevuto" e data arrivo di oggi.` +
      (lottoImport.trim() ? `\nLotto: ${lottoImport.trim()}` : '')
    )) {
      e.target.value = ''
      return
    }
    setImporting(true)
    try {
      const res = await workOrdersApi.importXlsx(file, 1, lottoImport.trim() || undefined)
      const { importati, aggiornati, saltati, errori } = res.data
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success(
        `Import completato: ${importati} nuove, ${aggiornati} aggiornate` +
        (saltati > 0 ? `, ${saltati} saltate` : '') +
        (errori > 0 ? `, ${errori} errori` : '')
      )
      if (res.data.dettaglio_errori?.length > 0) {
        console.warn('Errori import:', res.data.dettaglio_errori)
      }
    } catch (err: any) {
      toast.error('Errore import: ' + (err.response?.data?.detail || 'errore sconosciuto'))
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  return (
    <>
      {ConfirmModal}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="page-title">Pratiche</h1>
          <p className="page-subtitle">{total} pratiche totali</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => refetch()} className="btn-secondary" title="Aggiorna"><RefreshCw size={15} /></button>
          {!isTecnico && (
            <button onClick={() => setExportModalOpen(true)} className="btn-secondary" title="Esporta XLSX con filtri"><Download size={15} /><span className="hidden sm:inline ml-1">Export</span></button>
          )}
          {!isTecnico && (
            <button
              onClick={() => setEmailAccessiOpen(true)}
              className={`btn-secondary flex items-center gap-1.5${selected.size > 1 ? ' ring-2 ring-offset-1 ring-blue-400' : ''}`}
              title={selected.size > 1 ? `Invia email accessi per ${selected.size} pratiche selezionate` : 'Invia email accesso non consentito'}
            >
              ✉️ <span className="hidden sm:inline">Email Accessi</span>
              {selected.size > 1 && (
                <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">{selected.size}</span>
              )}
            </button>
          )}
          {!isTecnico && <button onClick={() => { setExportPianStep('filtri'); setExportPianModalOpen(true) }} className="btn-secondary" title="Esporta pianificazione interventi assegnati"><Download size={15} /><span className="hidden sm:inline ml-1">Pianificazione</span></button>}
          {!isTecnico && !isReadonly && (
            <label
              className={`btn-secondary cursor-pointer flex items-center gap-1.5${importing ? ' opacity-50 pointer-events-none' : ''}`}
              title="Importa pratiche da file ok_siti.xlsx"
            >
              <Upload size={15} />
              <span className="hidden sm:inline ml-1">{importing ? 'Import...' : 'Import'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={handleImport}
              />
            </label>
          )}
          {isAdmin && (
            <button
              onClick={() => { setModalitaTabella(v => !v); setRigheModificate({}) }}
              className="btn-secondary flex items-center gap-1.5"
              title="Modifica pratiche come tabella"
              style={modalitaTabella ? { borderColor: '#d97706', color: '#92400e', background: '#fffbeb' } : {}}
            >
              <Table2 size={15} /><span className="hidden sm:inline ml-1">{modalitaTabella ? 'Esci tabella' : 'Modifica tabella'}</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => confirmDelete('Cancellare TUTTE le pratiche? Operazione irreversibile.', () => deleteAllMutation.mutate())}
              className="btn-secondary" title="Cancella tutte le pratiche"
              style={{ color: '#ef4444' }}
              disabled={deleteAllMutation.isPending}
            >
              <Trash2 size={15} /><span className="hidden sm:inline ml-1">Cancella tutto</span>
            </button>
          )}
          {!isTecnico && !isReadonly && <button onClick={() => setShowCreateModal(true)} className="btn-primary"><Plus size={16} /><span className="hidden sm:inline ml-1">Nuova Pratica</span></button>}
        </div>
      </div>

      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Cerca per numero, denominazione, comune, regione..." value={search} onChange={(e) => { setSearch(e.target.value) }} />
          </div>
          <MultiSelectDropdown
            widthClass="w-36"
            placeholder="Tutti gli stati"
            options={STATI.map(s => ({ value: s, label: s }))}
            selected={statoFiltri}
            onChange={setStatoFiltri}
          />
          <MultiSelectDropdown
            widthClass="w-40"
            placeholder="Tutti i tecnici"
            options={tecniciBulk.map((t: any) => ({ value: String(t.id), label: `${t.nome} ${t.cognome}` }))}
            selected={tecnicoFiltri}
            onChange={setTecnicoFiltri}
          />
          <select className="select w-40" value={committenteFiltro} onChange={(e) => { setCommittenteFiltro(e.target.value) }}>
            <option value="">Tutti i committenti</option>
            {committenti?.map((c: any) => (
              <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
            ))}
          </select>
          <select
            className="select w-40"
            value={regioneFiltro}
            onChange={(e) => setRegioneFiltro(e.target.value)}
          >
            <option value="">Tutte le regioni</option>
            {regioniDisponibili.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <MultiSelectDropdown
            widthClass="w-40"
            placeholder="Tutti i comuni"
            options={comuniDisponibili.map(c => ({ value: c, label: c }))}
            selected={comuneFiltri}
            onChange={setComuneFiltri}
          />
          <select
            className="select w-36"
            value={lottoFiltro}
            onChange={(e) => setLottoFiltro(e.target.value)}
          >
            <option value="">Tutti i lotti</option>
            {lottiDisponibili.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <input
            type="date"
            className="input w-36"
            title="Appuntamento dal"
            value={dataFiltroFrom}
            onChange={e => setDataFiltroFrom(e.target.value)}
          />
          <input
            type="date"
            className="input w-36"
            title="Appuntamento al"
            value={dataFiltroTo}
            onChange={e => setDataFiltroTo(e.target.value)}
          />
          {(regioneFiltro || comuneFiltri.length > 0 || lottoFiltro || dataFiltroFrom || dataFiltroTo || oggiMode) && (
            <button
              className="btn-ghost text-xs flex items-center gap-1"
              title="Rimuovi filtri regione, comune, lotto e data"
              onClick={() => { setRegioneFiltro(''); setComuneFiltri([]); setLottoFiltro(''); setDataFiltroFrom(''); setDataFiltroTo(''); if (oggiMode) { setStatoFiltri([]); setSortBy('geo'); setSortDir('asc') } }}
            >
              <X size={12} /> Reset
            </button>
          )}
          <button
            onClick={() => {
              setStatoFiltri(['assegnato'])
              setDataFiltroFrom(todayStr)
              setDataFiltroTo(todayStr)
              setSortBy('tecnico_ordine')
              setSortDir('asc')
            }}
            className="btn-secondary text-sm flex items-center gap-1.5"
            style={oggiMode ? { borderColor: '#f97316', color: '#f97316', background: '#FFF7ED' } : {}}
            title="Mostra pratiche assegnate di oggi ordinate per tecnico e ordine intervento"
          >
            <Calendar size={13} /> Turno oggi
            {oggiMode && <span style={{ fontSize: 10, fontWeight: 700, background: '#f97316', color: '#fff', borderRadius: 99, padding: '1px 6px' }}>ATTIVO</span>}
          </button>
          <button
            onClick={apriPercorso}
            className="btn-secondary text-sm flex items-center gap-1.5"
            title="Visualizza percorso ottimizzato per le pratiche correnti"
          >
            <Navigation size={13} /> Percorso
          </button>
          <button
            onClick={runPercorsoOttimale}
            className="btn-secondary text-sm flex items-center gap-1.5"
            title="Organizza le pratiche selezionate per un giorno: per comune e via, verifica orari di chiusura, 30 minuti per intervento dalle 8:30"
          >
            🗺 Percorso ottimale
          </button>
          {isAdmin && (
            <button
              onClick={runCorreggiCodiceSito}
              disabled={correzioneCodiceRunning}
              className="btn-secondary text-sm flex items-center gap-1.5"
              title="Sulle pratiche filtrate: propone il numero pratica (frazionario) come Codice Sito dove è vuoto. Chiede conferma prima di salvare"
            >
              🏷 {correzioneCodiceRunning ? 'Correzione...' : 'Correggi Codice Sito'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={runCorreggiNomeSito}
              disabled={correzioneNomeRunning}
              className="btn-secondary text-sm flex items-center gap-1.5"
              title="Sulle pratiche filtrate: propone l'Ufficio (dalle note) come Nome Sito dove è vuoto. Chiede conferma prima di salvare"
            >
              🏢 {correzioneNomeRunning ? 'Correzione...' : 'Correggi Nome Sito'}
            </button>
          )}
        </div>
      </div>

      {!isTecnico && !isReadonly && selected.size > 0 && (
        <div className="card p-3 mb-3 flex flex-wrap items-center gap-3" style={{ borderColor: 'var(--signal)', background: 'color-mix(in srgb, var(--signal) 6%, white)' }}>
          <span className="text-sm font-semibold" style={{ color: 'var(--signal)' }}>{selected.size} selezionate</span>
          <div className="flex gap-2 flex-wrap flex-1 items-center">
            <button onClick={() => setShowBulkEditModal(true)} className="btn-primary text-sm flex items-center gap-1.5">
              <Pencil size={13} /> Modifica selezionate
            </button>
            <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />
            <button onClick={() => setShowBulkTipologia(true)} className="btn-secondary text-sm flex items-center gap-1.5">
              <Tag size={13} /> Tipologia
            </button>
            <button
              onClick={handleVerificaFrazionarioPoste}
              disabled={verificaLoading}
              className="btn-secondary text-sm flex items-center gap-1.5"
              title="Verifica comune/indirizzo/CAP interrogando in tempo reale blia.it (dati Poste Italiane) in base al frazionario">
              <Search size={13} /> {verificaLoading ? 'Verifica...' : 'Verifica vs Poste (live)'}
            </button>
            {isAdmin && (
              <button
                onClick={() => setRiparaIds(Array.from(selected))}
                className="btn-secondary text-sm flex items-center gap-1.5"
                title="Sulle pratiche selezionate: 1) confronto con elenco uffici postali abilitati (PDF), 2) CAP/provincia mancanti da Nominatim, 3) coordinate GPS. Chiede conferma a ogni passo">
                📮 Ripara CAP/Prov.
              </button>
            )}
            <button onClick={() => setSelected(new Set())} className="btn-ghost text-sm ml-auto flex items-center gap-1">
              <X size={13} /> Deseleziona
            </button>
          </div>
        </div>
      )}

      {modalitaTabella && (
        <div className="card p-3 mb-3 flex items-center gap-3" style={{ borderColor: '#d97706', background: '#fffbeb' }}>
          <Table2 size={16} style={{ color: '#92400e', flexShrink: 0 }} />
          <span className="text-sm font-semibold text-amber-900">
            Modalità modifica tabella
            {Object.keys(righeModificate).length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-200 text-amber-900">
                {Object.keys(righeModificate).length} righe modificate
              </span>
            )}
          </span>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => { setModalitaTabella(false); setRigheModificate({}) }} className="btn-secondary text-sm">
              <X size={13} /> Esci senza salvare
            </button>
            <button
              onClick={handleSalvaTabella}
              disabled={Object.keys(righeModificate).length === 0 || salvataggioLoading}
              className="btn-primary text-sm"
            >
              <Check size={13} /> {salvataggioLoading ? 'Salvataggio...' : `Salva ${Object.keys(righeModificate).length} modifiche`}
            </button>
          </div>
        </div>
      )}

      {modalitaTabella ? (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">N°</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Committente</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Sito / Comune</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Stato</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Lotto</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Data appt.</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Slot</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Ordine</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Tecnico</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Regione</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Provincia</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Comune</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Indirizzo</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Note</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((wo: any) => {
                  const mods = righeModificate[wo.id] ?? {}
                  const isModified = Object.keys(mods).length > 0
                  const val = (f: string) => f in mods ? mods[f] : wo[f]
                  const set = (f: string, v: any) => setCellaTabella(wo.id, f, v, wo[f])
                  const cellBg = (f: string): React.CSSProperties =>
                    f in mods ? { background: '#fef9c3' } : {}
                  return (
                    <tr key={wo.id} style={{ borderBottom: '1px solid #f1f5f9', background: isModified ? 'rgba(254,249,195,0.25)' : 'white' }}>
                      <td className="px-3 py-1.5 text-xs font-mono text-slate-500 whitespace-nowrap">{wo.numero_progressivo}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-600 max-w-[8rem] truncate">{wo.committente_nome}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-600 max-w-[10rem]">
                        <div className="truncate font-medium">{wo.denominazione_sito || wo.codice_sito || '—'}</div>
                        <div className="text-slate-400">{wo.cliente_comune}</div>
                      </td>
                      <td className="px-1 py-1" style={cellBg('stato')}>
                        <select className="select text-xs py-1 px-1.5 w-full min-w-[7rem]"
                          value={val('stato') ?? ''} onChange={e => set('stato', e.target.value)}>
                          {STATI.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1" style={cellBg('lotto')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[5rem]"
                          value={val('lotto') ?? ''} onChange={e => set('lotto', e.target.value || null)}
                          placeholder="—" />
                      </td>
                      <td className="px-1 py-1" style={cellBg('data_appuntamento')}>
                        <input type="date" className="input text-xs py-1 px-1.5 w-full min-w-[8rem]"
                          value={val('data_appuntamento') ?? ''}
                          onChange={e => set('data_appuntamento', e.target.value || null)} />
                      </td>
                      <td className="px-1 py-1" style={cellBg('slot_orario')}>
                        <select className="select text-xs py-1 px-1.5 w-full min-w-[6rem]"
                          value={val('slot_orario') ?? ''} onChange={e => set('slot_orario', e.target.value || null)}>
                          <option value="">—</option>
                          {SLOT_ORARI.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1 text-center">
                        {wo.ordine_giro != null
                          ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>#{wo.ordine_giro}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-1 py-1" style={cellBg('tecnico_id')}>
                        <select className="select text-xs py-1 px-1.5 w-full min-w-[8rem]"
                          value={val('tecnico_id') ?? ''} onChange={e => set('tecnico_id', e.target.value ? Number(e.target.value) : null)}>
                          <option value="">— nessuno —</option>
                          {tecniciBulk.map((t: any) => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1" style={cellBg('cliente_regione')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[7rem]"
                          value={val('cliente_regione') ?? ''} onChange={e => set('cliente_regione', e.target.value || null)}
                          placeholder="—" />
                      </td>
                      <td className="px-1 py-1" style={cellBg('cliente_provincia')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[4rem]"
                          value={val('cliente_provincia') ?? ''} onChange={e => set('cliente_provincia', e.target.value || null)}
                          placeholder="—" />
                      </td>
                      <td className="px-1 py-1" style={cellBg('cliente_comune')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[8rem]"
                          value={val('cliente_comune') ?? ''} onChange={e => set('cliente_comune', e.target.value || null)}
                          placeholder="—" />
                      </td>
                      <td className="px-1 py-1" style={cellBg('cliente_indirizzo')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[12rem]"
                          value={val('cliente_indirizzo') ?? ''} onChange={e => set('cliente_indirizzo', e.target.value || null)}
                          placeholder="—" />
                      </td>
                      <td className="px-1 py-1" style={cellBg('note')}>
                        <input type="text" className="input text-xs py-1 px-1.5 w-full min-w-[10rem]"
                          value={val('note') ?? ''} onChange={e => set('note', e.target.value || null)}
                          placeholder="—" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t text-xs text-slate-400">{filteredItems.length} pratiche</div>
        </div>
      ) : (
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border)' }}>
                <th className="px-3 py-3 w-8">
                  <input type="checkbox"
                    checked={filteredItems.length > 0 && selected.size === filteredItems.length}
                    onChange={toggleAll}
                    className="cursor-pointer"
                  />
                </th>
                {([
                  ['numero_progressivo', 'N°'],
                  ['committente', 'Committente'],
                  [null, 'Sito'],
                  ['regione', 'Reg/Prov'],
                  ['comune', 'Comune'],
                  ['indirizzo', 'Indirizzo'],
                  ['stato', 'Stato'],
                  [null, 'Lotto'],
                  ['tecnico', 'Tecnico'],
                  ['data_appuntamento', 'Appt.'],
                  ['ordine_giro', 'Ordine'],
                  [null, ''],
                ] as [string | null, string][]).map(([col, label], i) => (
                  <th key={i}
                    className="px-2 py-2 text-left"
                    style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                    onClick={() => col && toggleSort(col)}
                  >
                    <span className="inline-flex items-center">
                      {label}
                      {col && <SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={isTecnico ? 13 : 14} className="px-4 py-12 text-center" style={{ color: 'var(--text-500)' }}>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 rounded-full animate-spin" style={{ border: '2px solid var(--border-bright)', borderTopColor: 'var(--signal)' }} />
                    Caricamento...
                  </div>
                </td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><td colSpan={isTecnico ? 13 : 14} className="px-4 py-12 text-center" style={{ color: 'var(--text-500)' }}>Nessuna pratica trovata</td></tr>
              ) : filteredItems.map((wo) => (
                <tr key={wo.id} style={{ borderBottom: '1px solid var(--border)', background: selected.has(wo.id) ? 'color-mix(in srgb, var(--signal) 5%, white)' : (wo.ora_chiusura && wo.ora_chiusura > '14:00' ? '#f0fff4' : undefined) }}>
                  <td className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={selected.has(wo.id)} onChange={() => toggleSelect(wo.id)} className="cursor-pointer" />
                  </td>
                  <td className="px-2 py-1.5">
                    {editingNumeroId === wo.id ? (
                      <form
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        onSubmit={e => { e.preventDefault(); updateNumeroMutation.mutate({ id: wo.id, numero: editingNumeroVal }) }}
                      >
                        <input
                          autoFocus
                          value={editingNumeroVal}
                          onChange={e => setEditingNumeroVal(e.target.value)}
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: '0.7rem', fontWeight: 600,
                            width: 90, padding: '1px 4px',
                            border: '1px solid var(--signal)',
                            borderRadius: 4, outline: 'none',
                          }}
                          onKeyDown={e => e.key === 'Escape' && setEditingNumeroId(null)}
                        />
                        <button type="submit" style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          <Check size={13} />
                        </button>
                        <button type="button" onClick={() => setEditingNumeroId(null)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          <X size={13} />
                        </button>
                      </form>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Link to={`/pratiche/${wo.id}`} style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.7rem', fontWeight: 600,
                          color: 'var(--signal)',
                        }}>
                          {wo.numero_progressivo}
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => { setEditingNumeroId(wo.id); setEditingNumeroVal(wo.numero_progressivo) }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-500)', opacity: 0.5 }}
                            title="Modifica numero"
                          >
                            <Pencil size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 max-w-[90px] truncate" style={{ fontSize: 11, color: 'var(--text-400)' }} title={wo.committente_nome}>{wo.committente_nome}</td>
                  <td className="px-2 py-1.5" style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-500)' }}>{wo.codice_sito || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap" style={{ fontSize: 10, color: 'var(--text-400)' }}>
                    {[wo.cliente_regione, wo.cliente_provincia].filter(Boolean).join('/') || '—'}
                  </td>
                  <td className="px-2 py-1.5 max-w-[90px] truncate" style={{ fontSize: 11, color: 'var(--text-400)' }} title={wo.cliente_comune}>{wo.cliente_comune || '—'}</td>
                  <td className="px-2 py-1.5 max-w-[110px] truncate" style={{ fontSize: 10, color: 'var(--text-500)' }} title={wo.cliente_indirizzo}>{wo.cliente_indirizzo || '—'}</td>
                  <td className="px-2 py-1.5"><StatoBadge stato={wo.stato} /></td>
                  <td className="px-2 py-1.5 whitespace-nowrap" style={{ fontSize: 10, color: 'var(--text-500)' }}>
                    {wo.lotto ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 99,
                        fontSize: 10, fontWeight: 600, background: '#7c3aed22', color: '#7c3aed',
                      }}>{wo.lotto}</span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1.5 max-w-[90px] truncate" style={{ fontSize: 11, color: 'var(--text-400)' }} title={wo.tecnico_nome}>{wo.tecnico_nome || <span style={{ color: 'var(--text-600)' }}>—</span>}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap" style={{ fontSize: 10, color: 'var(--text-500)', fontFamily: 'monospace' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span>{wo.data_appuntamento ? format(new Date(wo.data_appuntamento), 'dd/MM/yy', { locale: it }) : '—'}</span>
                      {(wo.ora_apertura || wo.ora_chiusura) && <AppuntamentoChip wo={wo} />}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      min={1}
                      value={ordiniLocali[wo.id] !== undefined ? ordiniLocali[wo.id] : (wo.ordine_giro ?? '')}
                      onChange={e => setOrdiniLocali(prev => ({ ...prev, [wo.id]: e.target.value }))}
                      onBlur={e => saveOrdine(wo.id, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur() } }}
                      placeholder="—"
                      style={{
                        width: 44, textAlign: 'center', fontSize: 12, fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace",
                        padding: '2px 4px', border: '1px solid var(--border)',
                        borderRadius: 6, outline: 'none', background: wo.ordine_giro ? '#eff6ff' : '#f8fafc',
                        color: wo.ordine_giro ? '#1d4ed8' : 'var(--text-400)',
                        MozAppearance: 'textfield',
                      }}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {!isReadonly && (
                        <button
                          onClick={() => setPianificaWO(wo)}
                          className="btn-ghost px-2 py-1"
                          title="Pianifica data / ora / tipologia"
                        >
                          <CalendarClock size={14} style={{ color: 'var(--signal)' }} />
                        </button>
                      )}
                      <Link to={`/pratiche/${wo.id}`} className="btn-ghost px-2 py-1"><Eye size={14} /></Link>
                      {['superadmin','admin','dispatcher'].includes(user?.role ?? '') && (
                        <button
                          onClick={() => {
                            confirmDelete(`Eliminare la pratica ${wo.numero_progressivo}? Azione irreversibile.`, () => deleteOneMutation.mutate(wo.id))
                          }}
                          disabled={deleteOneMutation.isPending}
                          className="btn-ghost px-2 py-1"
                          title="Elimina pratica"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t text-sm" style={{ color: 'var(--text-500)' }}>
          <span>
            {filteredItems.length !== total
              ? <>{filteredItems.length} di {total} risultati</>
              : <>{total} risultati</>
            }
          </span>
        </div>
      </div>
      )}

      {emailAccessiOpen && (
        <EmailAccessiModal
          pratichePreselezionate={filteredItems
            .filter((w: any) => selected.has(w.id))
            .map((wo: any) => ({
              wo_id: wo.id,
              numero_progressivo: wo.numero_progressivo || '',
              codice_sito: wo.codice_sito || '',
              denominazione_sito: wo.denominazione_sito || '',
              regione: wo.cliente_regione || '',
              provincia: wo.cliente_provincia || '',
              comune: wo.cliente_comune || '',
              indirizzo: wo.cliente_indirizzo || '',
              data_appuntamento: wo.data_appuntamento || '',
              slot_orario: wo.slot_orario || wo.ore_appuntamento || '',
              tecnico_nome: wo.tecnico_nome || '',
            }))}
          onClose={() => setEmailAccessiOpen(false)}
        />
      )}

      {exportModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">⬇️ Export Pratiche</h3>
            <p className="text-sm text-gray-500 mb-4">
              Esporta le pratiche in XLSX, con orari apertura giornalieri del mese selezionato.
            </p>
            <div className="space-y-3">
              <div>
                <label className="label">Mese</label>
                <input
                  type="month"
                  className="input w-full"
                  value={exportMese}
                  onChange={(e) => setExportMese(e.target.value)}
                  title="Mese da esportare (orari apertura giornalieri)"
                />
              </div>
              <div>
                <label className="label">Regione</label>
                <select className="select w-full" value={exportRegione} onChange={(e) => setExportRegione(e.target.value)}>
                  <option value="">Tutte le regioni</option>
                  {regioniDisponibili.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Tecnico</label>
                <select className="select w-full" value={exportTecnicoId} onChange={(e) => setExportTecnicoId(e.target.value)}>
                  <option value="">Tutti i tecnici</option>
                  {tecniciBulk.map((t: any) => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setExportModalOpen(false)} className="btn-secondary flex-1">Annulla</button>
              <button onClick={handleExport} disabled={exportLoading} className="btn-primary flex-1">
                {exportLoading ? '⏳ Download...' : '⬇️ Scarica XLSX'}
              </button>
            </div>
          </div>
        </div>
      )}

      {exportPianModalOpen && exportPianStep === 'destinatari' && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setExportPianModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {exportPianCanale === 'whatsapp' ? '💬 Invia Pianificazione via WhatsApp' : '✉️ Invia Pianificazione via Email'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">Seleziona i destinatari (anagrafica utenti)</p>
            <div className="space-y-1.5 max-h-72 overflow-auto mb-4">
              {exportPianContatti.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  {exportPianCanale === 'whatsapp' ? 'Nessun utente con numero WhatsApp configurato' : 'Nessun utente con email configurata'}
                </p>
              ) : (
                exportPianContatti.map(c => (
                  <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={exportPianDestinatari.has(c.id)}
                      onChange={() => toggleDestinatarioPian(c.id)}
                    />
                    <span>{c.nome} {c.cognome}</span>
                    <span className="text-xs text-gray-400 ml-auto">{exportPianCanale === 'whatsapp' ? c.telefono_whatsapp : c.email}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setExportPianStep('filtri')} className="btn-secondary flex-1">Indietro</button>
              <button
                onClick={handleInviaPian}
                disabled={exportPianDestinatari.size === 0 || exportPianSending}
                className="btn-primary flex-1"
              >
                {exportPianSending ? 'Invio...' : `Invia (${exportPianDestinatari.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {exportPianModalOpen && exportPianStep === 'filtri' && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">📋 Export Pianificazione</h3>
            <p className="text-sm text-gray-500 mb-4">
              Esporta tutte le pratiche in stato <strong>assegnato</strong> nel periodo selezionato.
            </p>
            <div className="space-y-3">
              <div>
                <label className="label">Data da</label>
                <input type="date" value={exportPianDa} onChange={e => setExportPianDa(e.target.value)} className="input w-full" />
              </div>
              <div>
                <label className="label">Data a</label>
                <input type="date" value={exportPianA} onChange={e => setExportPianA(e.target.value)} className="input w-full" />
              </div>
              <div>
                <label className="label">Regioni</label>
                <MultiSelectDropdown
                  widthClass="w-full"
                  placeholder="Tutte le regioni"
                  options={regioniDisponibili.map(r => ({ value: r, label: r }))}
                  selected={exportPianRegioni}
                  onChange={setExportPianRegioni}
                />
              </div>
              <div>
                <label className="label">Tecnici</label>
                <MultiSelectDropdown
                  widthClass="w-full"
                  placeholder="Tutti i tecnici"
                  options={tecniciBulk.map((t: any) => ({ value: String(t.id), label: `${t.nome} ${t.cognome}` }))}
                  selected={exportPianTecnicoIds}
                  onChange={setExportPianTecnicoIds}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={() => setExportPianModalOpen(false)} className="btn-secondary flex-1">Annulla</button>
              <button onClick={() => apriDestinatariPian('email')} disabled={!exportPianDa || !exportPianA} className="btn-secondary flex-1">
                ✉️ Email
              </button>
              <button onClick={() => apriDestinatariPian('whatsapp')} disabled={!exportPianDa || !exportPianA} className="btn-secondary flex-1">
                💬 WhatsApp
              </button>
              <button
                onClick={handleExportPianificazione}
                disabled={!exportPianDa || !exportPianA || exportPianLoading}
                className="btn-primary flex-1"
              >
                {exportPianLoading ? '⏳ Download...' : '⬇️ Scarica XLSX'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreatePraticaModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            queryClient.invalidateQueries({ queryKey: ['work-orders'] })
            toast.success('Pratica creata!')
          }}
        />
      )}

      {pianificaWO && (
        <PianificaModal
          wo={pianificaWO}
          onClose={() => setPianificaWO(null)}
          onSuccess={() => {
            setPianificaWO(null)
            queryClient.invalidateQueries({ queryKey: ['work-orders'] })
          }}
        />
      )}

      {showBulkTipologia && (
        <BulkTipologiaModal
          selectedIds={Array.from(selected)}
          onClose={() => setShowBulkTipologia(false)}
          onSuccess={() => {
            setShowBulkTipologia(false)
            setSelected(new Set())
            queryClient.invalidateQueries({ queryKey: ['work-orders'] })
          }}
        />
      )}

      {giroIds && (
        <PianificaGiroModal
          ids={giroIds}
          dataIniziale={dataGiroIniziale()}
          onClose={() => setGiroIds(null)}
          onSaved={() => { setGiroIds(null); setSortBy('ordine_giro'); setSortDir('asc') }}
        />
      )}

      {riparaIds && (
        <RiparaIndirizziWizard ids={riparaIds} onClose={() => setRiparaIds(null)} />
      )}

      {anteprima && (
        <VerificaFrazionarioModal
          {...anteprima}
          onClose={() => setAnteprima(null)}
          onSuccess={() => {
            setAnteprima(null)
            setSelected(new Set())
            queryClient.invalidateQueries({ queryKey: ['work-orders'] })
          }}
        />
      )}

      {showBulkEditModal && (
        <BulkEditModal
          selectedIds={Array.from(selected)}
          tecnici={tecniciBulk}
          onClose={() => setShowBulkEditModal(false)}
          onSuccess={() => {
            setShowBulkEditModal(false)
            setSelected(new Set())
            queryClient.invalidateQueries({ queryKey: ['work-orders'] })
          }}
        />
      )}

      {percorsoOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px 12px 40px' }}
          onClick={e => { if (e.target === e.currentTarget) setPercorsoOpen(false) }}
        >
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden', marginTop: 16 }}>
            <div style={{ background: '#1E40AF', padding: '16px 16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Navigation size={18} /> Percorso Interventi
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                  {pratichePercorso.length} pratiche{selected.size > 0 ? ' selezionate' : ' filtrate'} · ordine_giro · 35 min/intervento
                </div>
              </div>
              <button onClick={() => setPercorsoOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#fff' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '16px 16px 20px' }}>
              {percorsoStatus === 'loading' && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #dbeafe', borderTopColor: '#1E40AF', animation: 'spin 0.7s linear infinite' }} />
                  <span style={{ fontSize: 14, color: '#6b7280' }}>Acquisizione posizione GPS...</span>
                </div>
              )}
              {percorsoStatus === 'no_gps' && (() => {
                const mapsAddr = (wo: any) => {
                  if (wo.cliente_lat && wo.cliente_lng) return `https://www.google.com/maps/dir/?api=1&destination=${wo.cliente_lat},${wo.cliente_lng}&travelmode=driving`
                  const addr = [wo.cliente_indirizzo, wo.cliente_comune, wo.cliente_cap].filter(Boolean).join(', ')
                  if (!addr) return null
                  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=driving`
                }
                const tuttoIlPercorso = (() => {
                  const pts = pratichePercorso.map((wo: any) => [wo.cliente_indirizzo, wo.cliente_comune].filter(Boolean).join(', ')).filter(Boolean)
                  if (!pts.length) return null
                  const dest = encodeURIComponent(pts[pts.length - 1])
                  const wps = pts.slice(0, -1).map(encodeURIComponent).join('|')
                  const base = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`
                  return wps ? `${base}&waypoints=${wps}` : base
                })()
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                      <AlertTriangle size={14} color="#d97706" />
                      <span style={{ fontSize: 12, color: '#92400e' }}>GPS non disponibile — distanze non calcolabili. Usa Maps per la navigazione.</span>
                    </div>
                    {pratichePercorso.map((wo: any, i: number) => {
                      const url = mapsAddr(wo)
                      const addr = [wo.cliente_indirizzo, wo.cliente_comune].filter(Boolean).join(', ')
                      return (
                        <div key={wo.id} style={{ borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', overflow: 'hidden' }}>
                          <div style={{ padding: '9px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#1E40AF' }}>{wo.numero_progressivo}</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.denominazione_sito || wo.cliente_comune || '—'}</div>
                              {addr && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{addr}</div>}
                            </div>
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, padding: '5px 10px', background: '#1E40AF', color: '#fff', borderRadius: 7, fontSize: 11, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Navigation size={11} /> Maps
                              </a>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    {tuttoIlPercorso && (
                      <a href={tuttoIlPercorso} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', background: '#1E40AF', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none', marginTop: 4 }}>
                        <Navigation size={15} /> Apri percorso completo in Maps
                      </a>
                    )}
                  </div>
                )
              })()}
              {percorsoStatus === 'ready' && percorsoGps && (() => {
                const ottimaleOrdinato = ottimizzaOrdineP(percorsoGps, pratichePercorso)
                const attualeOrdinato = [...pratichePercorso]
                const isOttimale = percorsoTab === 'ottimale'
                const orderedWos = isOttimale ? ottimaleOrdinato : attualeOrdinato
                const { steps, ritorno, totKm } = calcolaStepsP(percorsoGps, orderedWos)
                const { totKm: totKmAttuale } = calcolaStepsP(percorsoGps, attualeOrdinato)
                const { totKm: totKmOttimale } = calcolaStepsP(percorsoGps, ottimaleOrdinato)
                const risparmioKm = totKmAttuale - totKmOttimale
                const mapUrl = mapsUrlP(percorsoGps, orderedWos)
                const posizioneAttuale: Record<number, number> = {}
                attualeOrdinato.forEach((w: any, i: number) => { posizioneAttuale[w.id] = i + 1 })
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {percorsoGpsFallback && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                        <AlertTriangle size={14} color="#d97706" />
                        <span style={{ fontSize: 12, color: '#92400e' }}>GPS non disponibile — posizione calcolata dal centroide delle pratiche.</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setPercorsoTab('ottimale')} style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${percorsoTab === 'ottimale' ? '#1E40AF' : '#e2e8f0'}`, background: percorsoTab === 'ottimale' ? '#eff6ff' : '#fff', color: percorsoTab === 'ottimale' ? '#1E40AF' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        Ottimale {risparmioKm > 0.5 && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 99, padding: '1px 5px', marginLeft: 4 }}>-{risparmioKm.toFixed(0)}km</span>}
                      </button>
                      <button onClick={() => setPercorsoTab('attuale')} style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${percorsoTab === 'attuale' ? '#1E40AF' : '#e2e8f0'}`, background: percorsoTab === 'attuale' ? '#eff6ff' : '#fff', color: percorsoTab === 'attuale' ? '#1E40AF' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        Ordine attuale
                      </button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>Totale: {totKm.toFixed(1)} km</span>
                      <span style={{ color: '#475569' }}>{steps.length} interventi</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {steps.map((step: any, i: number) => {
                        const { wo, distanza_km, tempo_viaggio_min, ora_arrivo, ora_fine, chiusura_ok, margine_min } = step
                        const posAttuale = posizioneAttuale[wo.id]; const posNuova = i + 1
                        const cambiato = isOttimale && posAttuale !== posNuova
                        let chiusuraBg = '#f8fafc'; let chiusuraBorder = '#e2e8f0'; let chiusuraColor = '#6b7280'
                        if (chiusura_ok === false) { chiusuraBg = '#fef2f2'; chiusuraBorder = '#fca5a5'; chiusuraColor = '#dc2626' }
                        else if (chiusura_ok === true && margine_min != null && margine_min < 30) { chiusuraBg = '#fffbeb'; chiusuraBorder = '#fcd34d'; chiusuraColor = '#d97706' }
                        else if (chiusura_ok === true) { chiusuraBg = '#f0fdf4'; chiusuraBorder = '#bbf7d0'; chiusuraColor = '#16a34a' }
                        return (
                          <div key={wo.id} style={{ borderRadius: 12, border: `1.5px solid ${cambiato ? '#c7d2fe' : chiusuraBorder}`, background: cambiato ? '#eef2ff' : chiusuraBg, overflow: 'hidden' }}>
                            {distanza_km != null
                              ? <div style={{ padding: '4px 10px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Navigation size={10} color="#94a3b8" />
                                  <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{distanza_km.toFixed(1)} km · ~{tempo_viaggio_min} min</span>
                                </div>
                              : <div style={{ padding: '4px 10px', background: '#fef9c3', borderBottom: '1px solid #fde68a' }}>
                                  <span style={{ fontSize: 10, color: '#a16207' }}>⚠ Nessuna coordinata GPS</span>
                                </div>
                            }
                            <div style={{ padding: '9px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: cambiato ? '#4f46e5' : '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff' }}>{posNuova}</div>
                                {cambiato && <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600 }}>era {posAttuale}</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#1E40AF' }}>{wo.numero_progressivo}</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.denominazione_sito || wo.cliente_comune || '—'}</div>
                                {wo.tecnico_nome && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>👤 {wo.tecnico_nome}</div>}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>▶ {fmtHM(ora_arrivo)}</span>
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: '#f3f4f6', color: '#374151', fontWeight: 600 }}>⏹ {fmtHM(ora_fine)}</span>
                                  {wo.ora_chiusura && (
                                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: chiusuraBg, color: chiusuraColor, fontWeight: 700, border: `1px solid ${chiusuraBorder}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {chiusura_ok === false ? <><AlertTriangle size={9} /> {wo.ora_chiusura} IN RITARDO</>
                                        : margine_min != null && margine_min < 30 ? <><AlertTriangle size={9} /> {wo.ora_chiusura} {margine_min}min</>
                                        : <><CheckCircle size={9} /> {wo.ora_chiusura} OK</>}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      {ritorno && !percorsoGpsFallback && (
                        <div style={{ padding: '4px 10px', background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Home size={10} color="#94a3b8" />
                          <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Ritorno · {ritorno.distanza_km.toFixed(1)} km · ~{ritorno.tempo_viaggio_min} min · arrivo {fmtHM(ritorno.ora_arrivo)}</span>
                        </div>
                      )}
                    </div>
                    {mapUrl && (
                      <a href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', background: '#1E40AF', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none', marginTop: 4 }}>
                        <Navigation size={15} /> Apri in Google Maps
                      </a>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function CreatePraticaModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { user: authUser } = useAuth()
  const isTecnicoModal = ['tecnico', 'tecnico_senior'].includes(authUser?.role ?? '')
  const [form, setForm] = useState({
    committente_id: '',
    codice_sito: '',
    cliente_regione: '',
    denominazione_sito: '',
    ufficio: '',
    cliente_indirizzo: '',
    cliente_comune: '',
    cliente_provincia: '',
    cliente_cap: '',
    stato: 'ricevuto',
    tecnico_id: '',
    data_arrivo: new Date().toISOString().slice(0, 10),
    data_appuntamento: '',
    ore_appuntamento: '',
    materiale_installato: '',
    note: '',
    tipologia: '',
    priorita: 'normale',
  })

  const f = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }))

  const { data: committenti } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  const { data: tecnici } = useQuery({
    queryKey: ['tecnici-select'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  const { data: listino } = useQuery({
    queryKey: ['listino', form.committente_id],
    queryFn: () => committentiApi.getListino(Number(form.committente_id)).then(r => r.data),
    enabled: !!form.committente_id,
  })

  const mutation = useMutation({
    mutationFn: (data: any) => workOrdersApi.create({
      ...data,
      committente_id: Number(data.committente_id),
      tecnico_id: data.tecnico_id ? Number(data.tecnico_id) : undefined,
      note: data.ufficio
        ? `Ufficio: ${data.ufficio}` + (data.note ? ` | ${data.note}` : '')
        : data.note,
    }),
    onSuccess,
    onError: () => toast.error('Errore nella creazione'),
  })

  const vociListino: any[] = (listino as any)?.items ?? listino ?? []

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Nuova Pratica</h2>
          <button onClick={onClose} className="btn-ghost px-2">✕</button>
        </div>

        <form
          onSubmit={e => { e.preventDefault(); mutation.mutate(form) }}
          className="p-5 space-y-4"
        >
          {/* ── Sezione anagrafica sito ── */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Dati Sito (come da file import)
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Committente *</label>
                <select
                  className="select"
                  value={form.committente_id}
                  onChange={e => setForm(prev => ({ ...prev, committente_id: e.target.value, tipologia: '' }))}
                  required
                >
                  <option value="">Seleziona...</option>
                  {committenti?.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Frazionario (Codice Sito) *</label>
                <input
                  className="input"
                  placeholder="es. 01002"
                  value={form.codice_sito}
                  onChange={e => f('codice_sito', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Regione</label>
                <input
                  className="input"
                  placeholder="es. PIEMONTE"
                  value={form.cliente_regione}
                  onChange={e => f('cliente_regione', e.target.value)}
                />
              </div>
              <div>
                <label className="label">Filiale (Denominazione)</label>
                <input
                  className="input"
                  placeholder="es. ALESSANDRIA 2"
                  value={form.denominazione_sito}
                  onChange={e => f('denominazione_sito', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="label">Ufficio</label>
              <input
                className="input"
                placeholder="es. ACQUI TERME"
                value={form.ufficio}
                onChange={e => f('ufficio', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Indirizzo *</label>
              <input
                className="input"
                placeholder="es. VIA ERMENEGILDO TRUCCO 27"
                value={form.cliente_indirizzo}
                onChange={e => f('cliente_indirizzo', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="label">Comune *</label>
                <input
                  className="input"
                  placeholder="es. ACQUI TERME"
                  value={form.cliente_comune}
                  onChange={e => f('cliente_comune', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label">Sigla Prov.</label>
                <input
                  className="input"
                  placeholder="es. AL"
                  maxLength={2}
                  value={form.cliente_provincia}
                  onChange={e => f('cliente_provincia', e.target.value.toUpperCase())}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">CAP</label>
                <input
                  className="input"
                  placeholder="es. 15011"
                  maxLength={5}
                  value={form.cliente_cap}
                  onChange={e => f('cliente_cap', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Sezione operativa ── */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Dati Operativi
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Stato</label>
                <select className="select" value={form.stato} onChange={e => f('stato', e.target.value)}>
                  {STATI.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Tecnico Assegnato</label>
                <select className="select" value={form.tecnico_id} onChange={e => f('tecnico_id', e.target.value)}>
                  <option value="">Non assegnato</option>
                  {tecnici?.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Data Arrivo</label>
                <input type="date" className="input" value={form.data_arrivo} onChange={e => f('data_arrivo', e.target.value)} />
              </div>
              <div>
                <label className="label">Data Appuntamento</label>
                <input type="date" className="input" value={form.data_appuntamento} onChange={e => f('data_appuntamento', e.target.value)} />
              </div>
              <div>
                <label className="label">Ora</label>
                <input type="time" className="input" value={form.ore_appuntamento} onChange={e => f('ore_appuntamento', e.target.value)} />
              </div>
            </div>

            <div>
              <label className="label">
                Tipologia
                {vociListino.length > 0 && (
                  <span className="text-blue-500 ml-1 text-xs">
                    (da listino {committenti?.find((c: any) => String(c.id) === form.committente_id)?.ragione_sociale})
                  </span>
                )}
              </label>
              <select
                className="select"
                value={form.tipologia}
                onChange={e => f('tipologia', e.target.value)}
                disabled={!form.committente_id}
              >
                <option value="">
                  {!form.committente_id ? 'Seleziona prima il committente' : 'Seleziona tipologia...'}
                </option>
                {vociListino.filter((v: any) => v.is_active !== false).map((v: any) => (
                  <option key={v.id} value={v.descrizione}>
                    {v.descrizione}{!isTecnicoModal && v.netto > 0 ? ` — €${v.netto}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Materiale Installato</label>
              <input
                className="input"
                placeholder="es. ONT Huawei HG8010H"
                value={form.materiale_installato}
                onChange={e => f('materiale_installato', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Note</label>
              <textarea className="input" rows={2} value={form.note} onChange={e => f('note', e.target.value)} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Creazione...' : 'Crea Pratica'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Modal Pianifica (assegna data / ora / tipologia a pratica esistente) ────
function PianificaModal({ wo, onClose, onSuccess }: { wo: any; onClose: () => void; onSuccess: () => void }) {
  const { user: authUser } = useAuth()
  const isTecnicoModal = ['tecnico', 'tecnico_senior'].includes(authUser?.role ?? '')
  const [data, setData] = useState(wo.data_appuntamento ? wo.data_appuntamento.slice(0, 10) : '')
  const [ora, setOra]   = useState(wo.ore_appuntamento || '')
  const [tipologia, setTipologia] = useState(wo.tipologia || '')

  const frazionario = wo.codice_sito || ''

  // Orari apertura ufficio postale per la data selezionata
  const { data: orariUfficio, isPending: orariPending } = useQuery({
    queryKey: ['orari-apertura', frazionario, data],
    queryFn: () => orariAperturaApi.getOrari(frazionario, data).then(r => r.data),
    enabled: !!frazionario && !!data,
    retry: false,
  })

  // Controlla se l'ora inserita è fuori orario apertura
  const oraFuoriOrario = (() => {
    if (!orariUfficio || orariUfficio.chiuso || !ora || !orariUfficio.ora_apertura || !orariUfficio.ora_chiusura) return false
    const [oh, om] = ora.split(':').map(Number)
    const [ah, am] = orariUfficio.ora_apertura.slice(0, 5).split(':').map(Number)
    const [ch, cm] = orariUfficio.ora_chiusura.slice(0, 5).split(':').map(Number)
    const minOra = oh * 60 + om
    const minAp = ah * 60 + am
    const minCh = ch * 60 + cm
    return minOra < minAp || minOra > minCh
  })()

  const { data: listino, isLoading: listinoLoading } = useQuery({
    queryKey: ['listino', wo.committente_id],
    queryFn: () => committentiApi.getListino(wo.committente_id).then(r => r.data),
    enabled: !!wo.committente_id,
  })
  const voci: any[] = (listino as any)?.items ?? listino ?? []
  const vociAttive = voci.filter((v: any) => v.is_active !== false)

  const mutation = useMutation({
    mutationFn: () => workOrdersApi.update(wo.id, {
      data_appuntamento: data || null,
      ore_appuntamento:  ora  || null,
      tipologia:         tipologia || null,
    }),
    onSuccess: () => {
      toast.success('Pratica aggiornata')
      onSuccess()
    },
    onError: () => toast.error('Errore aggiornamento'),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="card w-full max-w-md animate-fade-up">
        <div className="p-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-100)' }}>Pianifica</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-500)' }}>
                {wo.numero_progressivo} — {wo.cliente_nome || wo.denominazione_sito || ''}
              </p>
            </div>
            <button onClick={onClose} className="btn-ghost px-2 py-1 text-sm">✕</button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Tipologia da listino */}
          <div>
            <label className="label">Tipologia</label>
            {listinoLoading ? (
              <div className="input flex items-center gap-2 text-sm" style={{ color: 'var(--text-500)' }}>
                <div className="w-3 h-3 rounded-full animate-spin" style={{ border: '2px solid var(--border-bright)', borderTopColor: 'var(--signal)' }} />
                Carico voci listino...
              </div>
            ) : vociAttive.length === 0 ? (
              <div>
                <input
                  className="input"
                  placeholder="Nessuna voce di listino — inserisci manualmente"
                  value={tipologia}
                  onChange={e => setTipologia(e.target.value)}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-500)' }}>
                  Il committente non ha voci di listino attive.
                </p>
              </div>
            ) : (
              <select className="select" value={tipologia} onChange={e => setTipologia(e.target.value)}>
                <option value="">— Seleziona tipologia —</option>
                {vociAttive.map((v: any) => (
                  <option key={v.id} value={v.descrizione}>
                    {v.descrizione}{!isTecnicoModal && v.netto > 0 ? ` — €${v.netto}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Data + Ora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Data Appuntamento</label>
              <input
                type="date"
                className="input"
                value={data}
                onChange={e => setData(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Ora</label>
              <input
                type="time"
                className="input"
                value={ora}
                onChange={e => setOra(e.target.value)}
              />
            </div>
          </div>

          {/* Orari apertura ufficio postale: visibile solo durante caricamento o quando l'orario è noto */}
          {frazionario && data && (orariPending || orariUfficio != null) && (
            <div className={`rounded-lg px-3 py-2.5 text-xs ${
              orariPending ? 'bg-slate-50 text-slate-400' :
              orariUfficio!.chiuso ? 'bg-red-50 border border-red-200 text-red-700' :
              'bg-blue-50 border border-blue-200 text-blue-800'
            }`}>
              {orariPending ? (
                <span>Verifica orari ufficio...</span>
              ) : orariUfficio!.chiuso ? (
                <span>⚠️ Ufficio <strong>{frazionario}</strong> chiuso il {format(new Date(data + 'T00:00:00'), 'dd/MM/yyyy', { locale: it })}</span>
              ) : (
                <span>
                  📍 Ufficio <strong>{frazionario}</strong> — {format(new Date(data + 'T00:00:00'), 'EEEE d MMM yyyy', { locale: it })}{' '}
                  🕐 <strong>{orariUfficio!.ora_apertura?.slice(0,5)} – {orariUfficio!.ora_chiusura?.slice(0,5)}</strong>
                </span>
              )}
              {oraFuoriOrario && (
                <div className="mt-1 text-red-600 font-semibold">
                  ⚠ L'ora {ora} è fuori dall'orario di apertura ({orariUfficio?.ora_apertura?.slice(0,5)}–{orariUfficio?.ora_chiusura?.slice(0,5)})
                </div>
              )}
            </div>
          )}

          {/* Riepilogo corrente */}
          {(wo.data_appuntamento || wo.tipologia) && (
            <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--mac-fill)', color: 'var(--text-500)' }}>
              Attuale: {wo.tipologia && <span className="font-medium">{wo.tipologia}</span>}
              {wo.tipologia && wo.data_appuntamento && ' · '}
              {wo.data_appuntamento && format(new Date(wo.data_appuntamento), 'dd/MM/yyyy', { locale: it })}
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── BulkTipologiaModal ────────────────────────────────────────────────────────
const TIPOLOGIE_STANDARD = [
  'installazione_ftth', 'installazione_fttc', 'manutenzione', 'collaudo',
  'riparazione', 'sopralluogo', 'attivazione', 'cessazione',
]

function BulkTipologiaModal({ selectedIds, onClose, onSuccess }: {
  selectedIds: number[]; onClose: () => void; onSuccess: () => void
}) {
  const { user: authUser } = useAuth()
  const isTecnicoModal = ['tecnico', 'tecnico_senior'].includes(authUser?.role ?? '')
  const qc = useQueryClient()
  const [committenteId, setCommittenteId] = useState('')
  const [tipologia, setTipologia] = useState('')

  const { data: committenti } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const { data: listino } = useQuery({
    queryKey: ['listino', committenteId],
    queryFn: () => committentiApi.getListino(Number(committenteId)).then(r => r.data),
    enabled: !!committenteId,
  })
  const voci: any[] = (listino as any)?.items ?? listino ?? []
  const vociAttive = voci.filter((v: any) => v.is_active !== false)

  const mutation = useMutation({
    mutationFn: () => workOrdersApi.bulkUpdate({ ids: selectedIds, tipologia }),
    onSuccess: () => {
      toast.success(`Tipologia aggiornata su ${selectedIds.length} pratiche`)
      onSuccess()
    },
    onError: () => toast.error('Errore aggiornamento'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card w-full max-w-md animate-fade-up">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><Tag size={16} className="text-blue-600" /> Cambia Tipologia</h2>
            <p className="text-xs text-slate-500 mt-0.5">Modifica tipologia per {selectedIds.length} pratiche selezionate</p>
          </div>
          <button onClick={onClose} className="btn-ghost px-2">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label">Committente (per listino)</label>
            <select className="select" value={committenteId} onChange={e => { setCommittenteId(e.target.value); setTipologia('') }}>
              <option value="">— Seleziona per vedere il listino —</option>
              {committenti?.map((c: any) => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Tipologia *</label>
            <select className="select" value={tipologia} onChange={e => setTipologia(e.target.value)} required>
              <option value="">— Seleziona —</option>
              {vociAttive.length > 0 && (
                <optgroup label="Listino committente">
                  {vociAttive.map((v: any) => (
                    <option key={v.id} value={v.descrizione}>{v.descrizione}{!isTecnicoModal && v.netto > 0 ? ` — €${v.netto}` : ''}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Tipologie standard">
                {TIPOLOGIE_STANDARD.map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!tipologia || mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Applico...' : `Applica a ${selectedIds.length} pratiche`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── BulkEditModal ─────────────────────────────────────────────────────────────
const SLOT_ORARI = [
  'AM', 'PM',
  '08:30-09:30', '09:30-10:30', '10:30-11:30', '11:30-12:30',
  '12:30-13:30', '13:30-14:30', '14:30-15:30', '15:30-16:30',
  '16:30-17:30', '17:30-18:30', '18:30-19:30',
]

function BulkEditModal({ selectedIds, tecnici, onClose, onSuccess }: {
  selectedIds: number[]; tecnici: any[]; onClose: () => void; onSuccess: () => void
}) {
  const [bulkStato, setBulkStato] = useState('')
  const [bulkTecnicoId, setBulkTecnicoId] = useState('')
  const [data, setData] = useState('')
  const [slot, setSlot] = useState('')
  const [mantieni, setMantieni] = useState(false)
  const [bulkNote, setBulkNote] = useState('')

  const hasChange = bulkStato || bulkTecnicoId !== '' || data || (!mantieni && slot)
  const noteObbligatoria = bulkStato === 'sospeso' || bulkStato === 'annullato'

  const mutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = { ids: selectedIds }
      if (bulkStato) payload.stato = bulkStato
      if (bulkNote.trim()) payload.note = bulkNote.trim()
      if (bulkTecnicoId !== '') payload.tecnico_id = bulkTecnicoId === '0' ? 0 : Number(bulkTecnicoId)
      if (data) payload.data_appuntamento = data
      if (!mantieni && slot) payload.slot_orario = slot
      payload.mantieni_slot = mantieni
      return workOrdersApi.bulkUpdate(payload as Parameters<typeof workOrdersApi.bulkUpdate>[0])
    },
    onSuccess: (res) => {
      toast.success(`${(res.data as { aggiornati: number }).aggiornati} pratiche aggiornate`)
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore aggiornamento bulk'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget && !mutation.isPending) onClose() }}>
      <div className="card w-full max-w-lg animate-fade-up">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><Pencil size={16} className="text-blue-600" /> Modifica Pratiche Selezionate</h2>
            <p className="text-xs text-slate-500 mt-0.5">Modifica {selectedIds.length} pratiche selezionate</p>
          </div>
          <button onClick={onClose} disabled={mutation.isPending} className="btn-ghost px-2">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Sezione 1 — Stato e Assegnazione */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Stato e Assegnazione</p>
            <div className="space-y-3">
              <div>
                <label className="label">Cambia stato</label>
                <select className="select" value={bulkStato} onChange={e => setBulkStato(e.target.value)}>
                  <option value="">— nessuna modifica —</option>
                  {STATI.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Assegna tecnico</label>
                <select className="select" value={bulkTecnicoId} onChange={e => setBulkTecnicoId(e.target.value)}>
                  <option value="">— nessuna modifica —</option>
                  <option value="0">Rimuovi assegnazione</option>
                  {tecnici.map((t: any) => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                </select>
              </div>
              {noteObbligatoria && (
                <div>
                  <label className="label">Note interne <span className="text-red-600">*</span></label>
                  <textarea
                    className="input"
                    rows={3}
                    value={bulkNote}
                    onChange={e => setBulkNote(e.target.value)}
                    placeholder={`Motivo del ${bulkStato === 'sospeso' ? 'sospensione' : 'annullamento'} (obbligatorio)...`}
                  />
                </div>
              )}
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--border)' }} />

          {/* Sezione 2 — Appuntamento */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Appuntamento</p>
            <div className="space-y-3">
              <div>
                <label className="label">Nuova data appuntamento</label>
                <input type="date" className="input" value={data} onChange={e => setData(e.target.value)} />
              </div>
              <div>
                <label className="label">Slot orario</label>
                <select className="select" value={slot} onChange={e => setSlot(e.target.value)} disabled={!data || mantieni}>
                  <option value="">— nessuna modifica —</option>
                  {SLOT_ORARI.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={mantieni} onChange={e => setMantieni(e.target.checked)} disabled={!data} />
                <span className="text-sm text-slate-700">Aggiorna solo la data, mantieni lo slot orario attuale</span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} disabled={mutation.isPending} className="btn-secondary flex-1">Annulla</button>
          <button
            onClick={() => {
              if (noteObbligatoria && !bulkNote.trim()) { toast.error('Le note interne sono obbligatorie'); return }
              mutation.mutate()
            }}
            disabled={!hasChange || mutation.isPending || (noteObbligatoria && !bulkNote.trim())}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Applico...' : `Applica a ${selectedIds.length} pratiche`}
          </button>
        </div>
      </div>
    </div>
  )
}
